import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import loadable from '@loadable/component';

// Utils
import getWindow from 'Utils/get-window';
import GlobalVars from 'Utils/global-vars';
import sentry from 'Utils/sentry';
import loadIMA from 'Utils/load-ima';
// import { getAdForVideo } from 'Utils/api/ads';

import ErrorBoundary from 'ComponentLibrary/errors/error-boundary';

// hocs
import withThanksSubscription from '../../hocs/withThanksSubscription';

import connector from 'Utils/connector';
import connectFirstPartyVideo from 'Webapp/shared/app/connectors/connectFirstPartyVideo';
import connectSendUsageEventWithSession from 'Webapp/shared/app/connectors/connectSendUsageEventWithSession';
import withIsIos from '../../hocs/withIsIos';
import withT from 'ComponentLibrary/hocs/withT';
import VideoTracker from 'Webapp/shared/utils/video-tracker';

let Videojs = () => null;
let VideojsContribAds = () => null;
let VideojsIMA = () => null;
let CanAutoplay = () => null;

if (!GlobalVars.isServer()) {
  Videojs = loadable.lib(() => import('video.js'));
  VideojsContribAds = loadable.lib(() => import('videojs-contrib-ads'));
  VideojsIMA = loadable.lib(() => import('videojs-ima'));
  CanAutoplay = loadable.lib(() => import('can-autoplay'));
}

// BEGIN BIG HACK
//
// TODO remove this hack
//
// After switching to webpack 5 we are seeing the refs for
// loadable.lib only working one time.  The second time the components
// created by loadable.lib were mounted, the ref callback was not ever
// being called.

// The idea behind this hack is that it works the first time, so we
// store it and access it that way in the future rather than getting
// it from the component ref each time

// FWIW we also tried updating the @loadable/* libraries and that did
// not help, seems like the issue hasn't been addressed.

// See:
// our switch to webpack 5
// https://github.com/Flipboard/web-universal/pull/3356/

// possible related bug / issue with loadable
// https://github.com/gregberge/loadable-components/issues/829

// object to store dynamic libs in these libs will be accessed by
// loadableLibs[key] in component logic
//
// :(
const loadableLibs = {};
const memoizeLoadableLib = (key) => (ref) => {
  // if we have dynamic lib at key arleady do nothing
  if (loadableLibs[key]) {
    return;
  }
  // if the ref looks like an import, store at key
  if (ref?.default) {
    loadableLibs[key] = ref.default;
  }
};
// END BIG HACK

class HTMLVideo extends Component {
  constructor(props) {
    super(props);
    this.videoElementRef = React.createRef();

    this.loadVideoJSTimer = null;
    this.requestedSignedMediaCookie = false;
    this.midrollTriggered = false;

    this.refreshSignedMediaCookie = this.refreshSignedMediaCookie.bind(this);
    this.setupVideo = this.setupVideo.bind(this);
    this.shouldShowAd = this.shouldShowAd.bind(this);
    this.onError = this.onError.bind(this);
    this.getSource = this.getSource.bind(this);
    this.sourceRequiresCredentials = this.sourceRequiresCredentials.bind(this);
    this.state = {
      adsResponse: null,
      setupComplete: false,
      adsBlocked: false,
    };
  }

  async componentDidMount() {
    loadIMA();
    if (this.sourceRequiresCredentials()) {
      await this.refreshSignedMediaCookie();
    }
    this.setupVideo();
    // getAdForVideo().then(adsResponse => this.setState({ adsResponse }));
  }

  componentDidUpdate(prevProps) {
    if (!this.player) {
      return;
    }
    const { play } = this.props;
    if (!this.player.seeking() && prevProps.play !== play) {
      this.player[play ? 'play' : 'pause']();
    }
  }

  componentWillUnmount() {
    if (this.player) {
      this.player.dispose();
    }
    if (this.fullscreenChangeHandler) {
      document.removeEventListener('fullscreenchange', this.fullscreenChangeHandler);
    }
    if (this.videoElementRef.current) {
      if (this.webkitBeginFullscreenHandler) {
        this.videoElementRef.current.removeEventListener('webkitbeginfullscreen', this.webkitBeginFullscreenHandler);
      }
      if (this.webkitEndFullscreenHandler) {
        this.videoElementRef.current.removeEventListener('webkitendfullscreen', this.webkitEndFullscreenHandler);
      }
    }
    getWindow().clearTimeout(this.loadVideoJSTimer);
    getWindow().clearTimeout(this.setupCompleteTimer);
    getWindow().clearTimeout(this.fullscreenResumeTimer);
  }

  async refreshSignedMediaCookie() {
    await this.props.refreshSignedMediaCookie();
    // If the above doesn't actually work, and the src in question
    // requires signed cookies, loading the media will fail.  If the
    // src does not require it, the video should still work
    this.requestedSignedMediaCookie = true;
  }

  // Returns { show, reason }. When not showing, `reason` sizes the invisible
  // upstream pre-roll loss — opportunities where no ad_request ever fires, so
  // they're absent from the ad funnel. 'not_eligible' = item.adPreRollOk is
  // false, which is where server-side gating (brand safety, domain, and the
  // missing-duration case Sami flagged) lands.
  evaluatePreRoll() {
    const {
      prerollAds,
      validThanksSubscription,
      shownVideoAd,
      adShowPercentage,
      videoAdsEnabled,
      forceVideoAds,
    } = this.props;
    if (!(videoAdsEnabled || forceVideoAds)) {
      return { show: false, reason: 'ads_disabled' };
    }
    if (!prerollAds) {
      return { show: false, reason: 'not_eligible' };
    }
    if (validThanksSubscription) {
      return { show: false, reason: 'subscriber' };
    }
    if (!shownVideoAd) {
      return { show: true, reason: null };
    }
    return Math.random() * 100 < adShowPercentage
      ? { show: true, reason: null }
      : { show: false, reason: 'frequency' };
  }

  shouldShowAd() {
    return this.evaluatePreRoll().show;
  }

  getSource() {
    const { source } = this.props;
    let src = source;

    // TODO here to debug / block VE problem, remove later
    if (src.indexOf('s3.amazonaws.com/ve1') > -1) {
      src = 'https://cdn.flipboard.com/dev_O/unavailable.mp4';
    }
    return src || '';
  }

  sourceRequiresCredentials() {
    return this.getSource().indexOf('ms.flipboard.com') > -1;
  }

  setupVideo() {
    const { isIos, isEmbedded, sendUsageEventWithSession } = this.props;
    // this is async loaded in footer-assets
    const googleIMAloaded = getWindow().google && getWindow().google.ima;
    // Don't wait on canAutoplay when rendering in iOS
    const autoplayNotReady = !isIos && !loadableLibs.canAutoplay;

    const credentialsNotReady = this.sourceRequiresCredentials()
      ? !this.requestedSignedMediaCookie
      : false;

    if (
      !googleIMAloaded ||
      !loadableLibs.videojs ||
      !loadableLibs.videojsContribAds ||
      !loadableLibs.videojsIMA ||
      credentialsNotReady ||
      autoplayNotReady
    ) {
      this.loadVideoJSTimer = getWindow().setTimeout(() => {
        this.setupVideo();
      }, 100);
      return;
    }

    if (!loadableLibs.videojs.getPlugin('ads')) {
      loadableLibs.videojs.registerPlugin('ads', loadableLibs.videojsContribAds);
    }

    // Register seek back/forward buttons as Video.js components
    const VjsButton = loadableLibs.videojs.getComponent('Button');
    if (!loadableLibs.videojs.getComponent('SeekBackBtn')) {
      const SeekBackBtn = loadableLibs.videojs.extend(VjsButton, {
        constructor: function constructor(player, options) {
          VjsButton.call(this, player, options);
          this.addClass('vjs-seek-back-btn');
          this.controlText('Go back 15 seconds');
        },
        handleClick: function handleClick() {
          const p = this.player();
          p.currentTime(Math.max(0, p.currentTime() - 15));
        },
      });
      loadableLibs.videojs.registerComponent('SeekBackBtn', SeekBackBtn);
    }
    if (!loadableLibs.videojs.getComponent('SeekFwdBtn')) {
      const SeekFwdBtn = loadableLibs.videojs.extend(VjsButton, {
        constructor: function constructor(player, options) {
          VjsButton.call(this, player, options);
          this.addClass('vjs-seek-fwd-btn');
          this.controlText('Skip ahead 30 seconds');
        },
        handleClick: function handleClick() {
          const p = this.player();
          const dur = p.duration() || Infinity;
          p.currentTime(Math.min(dur, p.currentTime() + 30));
        },
      });
      loadableLibs.videojs.registerComponent('SeekFwdBtn', SeekFwdBtn);
    }

    const {
      poster,
      videoJSOptions,
      track,
      videoTrackerOptions,
      videoEvents,
      videoAdTagUrl,
      overrideAdTagUrl,
      shownVideoAd,
      mimeType,
      t,
    } = this.props;

    const effectiveAdTagUrl = overrideAdTagUrl || videoAdTagUrl;
    const { show: showAd, reason: preRollSkipReason } = this.evaluatePreRoll();

    const src = this.getSource();

    this.player = loadableLibs.videojs(
      this.videoElementRef.current,
      {
        // black poster until we have decided what we are doing
        poster:
          'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABAQAAAAA3bvkkAAAACklEQVQI12NgAAAAAgAB4iG8MwAAAABJRU5ErkJggg==',
        fill: true,
        controls: true,
        ...(isEmbedded && { inactivityTimeout: 4000 }),
        ...(isEmbedded && {
          controlBar: {
            children: [
              'playToggle',
              'currentTimeDisplay',
              'SeekBackBtn',
              'progressControl',
              'SeekFwdBtn',
              'remainingTimeDisplay',
              'fullscreenToggle',
            ],
          },
        }),
        sources: [
          {
            src,
            type: mimeType,
            withCredentials: this.sourceRequiresCredentials(),
          },
        ],
        ...videoJSOptions,
      },
      () => {
        const video = this.videoElementRef.current;
        if (video) {
          if (track) {
            new VideoTracker(
              video,
              videoTrackerOptions,
              sendUsageEventWithSession,
            );
          }
        }

        const showVideoIos = () => {
          this.player.poster(poster);
          // HACK to remove spinner in iOS
          this.setupCompleteTimer = getWindow().setTimeout(() => {
            this.player.removeClass('vjs-waiting');
            this.setState({
              setupComplete: true,
            });
            if (this.props.autoPlay) {
              this.player.play();
            }
          }, 100);
        };
        const showVideoNonIos = () => {
          loadableLibs.canAutoplay.video({ muted: false }).then((result) => {
            this.setupCompleteTimer = getWindow().setTimeout(
              () =>
                this.setState({
                  setupComplete: true,
                }),
              100,
            );
            if (result.result === false || !this.props.autoPlay) {
              // if we can't autoplay show the poster
              this.player.poster(poster);
              return;
            }
            this.player.play();
          });
        };
        const showVideo = isIos ? showVideoIos : showVideoNonIos;

        // Web video-ad telemetry. iOS/Android ad outcomes are visible via GAM
        // Data Transfer, but web VAST outcomes are otherwise invisible — the
        // adserror handler below silently drops to content with no signal. Emit
        // a usage event at each IMA lifecycle stage so fill / impression / error
        // rates are measurable: ad_request = denominator, ad_impression =
        // rendered, ad_error / ad_nofill = the "no ad shown" cases.
        // Log the stable GAM ad unit (the `iu` param), not the full tag URL —
        // the URL carries cust_params/targeting that would bloat cardinality and
        // leak targeting context into analytics.
        let adUnit;
        try {
          adUnit =
            new URL(effectiveAdTagUrl).searchParams.get('iu') || undefined;
        } catch (e) {
          adUnit = undefined;
        }
        const logAdOutcome = (action, extra = {}) => {
          const { sendUsageEventWithSession, videoTrackerOptions = {} } =
            this.props;
          if (!sendUsageEventWithSession) {
            return;
          }
          // Dedup: IMA events ('ads-ad-started' etc.) can fire more than once
          // against a single player, over-counting impressions/errors vs
          // requests. This component owns one player for one video, so a set that
          // logs each action at most once keeps request/impression/error
          // reconcilable (no per-source reset needed).
          if (!this._adLoggedActions) {
            this._adLoggedActions = new Set();
          }
          if (this._adLoggedActions.has(action)) {
            return;
          }
          this._adLoggedActions.add(action);
          try {
            sendUsageEventWithSession({
              event_category: 'ad',
              event_action: action,
              // Keys must map to real usage_event columns (method / target_id /
              // item_type / url) to survive the pipeline — custom keys are dropped.
              event_data: {
                method: this.props.isEmbedded ? 'video_embed' : 'video_web',
                item_type: this.props.midrollTime > 0 ? 'midroll' : 'preroll',
                target_id: adUnit,
                item_id: videoTrackerOptions.item_id,
                section_id: videoTrackerOptions.section_id,
                item_partner_id: videoTrackerOptions.item_partner_id,
                ...extra,
              },
            });
          } catch (e) {
            sentry.captureException(e);
          }
        };

        const { midrollTime } = this.props;
        if (showAd) {
          // One ad_request per setup — the denominator. This player serves one
          // video, so the dedup set bounds each outcome to once and we don't need
          // a 'loadstart' reset (which would miss the initial source and re-arm
          // duplicate events on reloads). If this player is ever reused across
          // videos, reset the set in that explicit source-swap path instead.
          logAdOutcome('ad_request');
          this.player.on('adsready', () => logAdOutcome('ad_ready'));
          this.player.on('ads-ad-started', () => logAdOutcome('ad_impression'));
          this.player.on('ads-ad-ended', () => logAdOutcome('ad_complete'));
          this.player.on('nopreroll', () => logAdOutcome('ad_nofill'));
          this.player.on('adtimeout', () => logAdOutcome('ad_timeout'));
          this.player.on('adserror', (e) =>
            logAdOutcome('ad_error', {
              // event_url carries the IMA error message (mapped column).
              url:
                (e &&
                  ((e.data &&
                    e.data.AdError &&
                    e.data.AdError.getMessage &&
                    e.data.AdError.getMessage()) ||
                    e.message)) ||
                undefined,
            }),
          );

          if (midrollTime > 0) {
            // Midroll: start content immediately, ad triggers later
            showVideo();
            // Mark shownVideoAd when the midroll ad actually starts
            this.player.on('ads-ad-started', () => {
              if (!shownVideoAd) {
                this.props.setShownVideoAd(true);
              }
            });
          } else {
            // Preroll: wait until the ad is ready to play
            this.player.on('adsready', () => {
              showVideo();
              // set shownVideoAd if we think we actually have
              if (!shownVideoAd) {
                this.props.setShownVideoAd(true);
              }
            });
            // we didn't get an ad, go ahead and play
            this.player.on('adserror', showVideo);
          }
        } else {
          // Eligible video but pre-roll not requested — log why, so the
          // upstream loss (esp. 'not_eligible' = server-side gating incl.
          // missing duration) is measurable alongside the request-level funnel.
          logAdOutcome('preroll_skipped', { url: preRollSkipReason });
          showVideo();
        }
      },
    );

    if (isEmbedded) {
      // Tap on video area toggles play/pause and shows the control bar.
      // Video.js doesn't do this reliably on touch devices.
      this.player.on('touchend', (e) => {
        const target = e.target || e.srcElement;
        if (target && target.closest && target.closest('.vjs-control-bar')) {
          return;
        }
        if (this.player.paused()) {
          this.player.play();
        } else {
          this.player.pause();
        }
        this.player.userActive(true);
      });

      // Show control bar when paused
      this.player.on('pause', () => {
        this.player.userActive(true);
      });
    }

    // Fullscreen handling for embedded players.
    const videoEl = this.videoElementRef.current;
    if (isEmbedded && videoEl) {
      const fsButton = this.player.controlBar.getChild('fullscreenToggle');
      if (fsButton) {
        if (videoEl.webkitEnterFullscreen && !document.fullscreenEnabled) {
          // iOS WebView: the Fullscreen API (requestFullscreen) doesn't work.
          // Use webkitEnterFullscreen on the native video element instead.
          fsButton.handleClick = () => {
            videoEl.webkitEnterFullscreen();
          };
          // Resume playback after exiting iOS native fullscreen.
          // iOS fires pause BEFORE webkitendfullscreen, so we track
          // the playing state at fullscreen entry to know whether to
          // resume on exit.
          this.wasPlayingBeforeFullscreen = false;
          this.webkitBeginFullscreenHandler = () => {
            this.wasPlayingBeforeFullscreen = !this.player.paused();
            if (this.props.onFullscreenChange) {
              this.props.onFullscreenChange(true);
            }
          };
          videoEl.addEventListener('webkitbeginfullscreen', this.webkitBeginFullscreenHandler);
          this.webkitEndFullscreenHandler = () => {
            if (this.props.onFullscreenChange) {
              this.props.onFullscreenChange(false);
            }
            if (this.wasPlayingBeforeFullscreen) {
              this.fullscreenResumeTimer = getWindow().setTimeout(() => {
                if (this.player && this.player.paused()) {
                  this.player.play();
                }
              }, 200);
            }
          };
          videoEl.addEventListener('webkitendfullscreen', this.webkitEndFullscreenHandler);
        } else if (document.fullscreenEnabled) {
          // Web iframe: requestFullscreen on the player container only fills
          // the iframe, not the screen.  Request fullscreen on the document
          // element so the browser promotes the iframe to true fullscreen.
          fsButton.handleClick = () => {
            if (document.fullscreenElement) {
              document.exitFullscreen();
            } else {
              document.documentElement.requestFullscreen();
            }
          };
          // Sync Video.js fullscreen class with actual document fullscreen state
          this.fullscreenChangeHandler = () => {
            if (document.fullscreenElement) {
              this.player.addClass('vjs-fullscreen');
              if (this.props.onFullscreenChange) {
                this.props.onFullscreenChange(true);
              }
            } else {
              if (this.props.onFullscreenChange) {
                this.props.onFullscreenChange(false);
              }
              const wasPlaying = !this.player.paused();
              this.player.removeClass('vjs-fullscreen');
              // Browsers may transiently pause the video during fullscreen
              // exit, which triggers onPause → setState({play:false}) and
              // locks the player in a paused state.  Resume after the
              // transition settles.
              if (wasPlaying) {
                this.fullscreenResumeTimer = getWindow().setTimeout(() => {
                  if (this.player && this.player.paused()) {
                    this.player.play();
                  }
                }, 200);
              }
            }
          };
          document.addEventListener('fullscreenchange', this.fullscreenChangeHandler);
        }
      }
    } else if (videoEl && videoEl.webkitEnterFullscreen && !document.fullscreenEnabled) {
      // Non-embedded iOS fallback (legacy path)
      const fsButton = this.player.controlBar.getChild('fullscreenToggle');
      if (fsButton) {
        fsButton.handleClick = () => {
          videoEl.webkitEnterFullscreen();
        };
      }
    }

    this.player.on('ended', () => {
      this.player.poster(poster);
      this.player.hasStarted(false);
    });

    this.player.on('error', () => {
      if (GlobalVars.isDevelopment) {
        getWindow().alert(
          'Unable to play video.  This is development, is this a signed media thing? Do you have signed media cookies?  Visit this same page in production to get those.',
        );
      }
    });

    Object.keys(videoEvents).forEach((key) =>
      this.player.on(key, videoEvents[key]),
    );

    if (showAd) {
      const { midrollTime } = this.props;
      if (midrollTime > 0) {
        // Midroll: initialize IMA without adTagUrl so no preroll fires
        this.player.ima({
          adLabel: t('advertisement'),
          adWillAutoPlay: true,
          adWillPlayMuted: !!this.props.muted,
        });
        // Trigger midroll ad when playback reaches the specified time
        this.player.on('timeupdate', () => {
          if (
            !this.midrollTriggered &&
            this.player.currentTime() >= midrollTime
          ) {
            this.midrollTriggered = true;
            this.midrollResumeTime = this.player.currentTime();
            this.player.ima.changeAdTag(effectiveAdTagUrl);
            this.player.ima.requestAds();
          }
        });
        // Safety net: restore playback position if the SDK resets it
        this.player.on('contentresumed', () => {
          if (
            this.midrollResumeTime &&
            this.player.currentTime() < this.midrollResumeTime - 1
          ) {
            this.player.currentTime(this.midrollResumeTime);
          }
          this.midrollResumeTime = null;
        });
      } else {
        // Preroll: standard IMA initialization with adTagUrl
        this.player.ima({
          adLabel: t('advertisement'),
          adTagUrl: effectiveAdTagUrl,
          adWillAutoPlay: this.props.autoPlay,
          adWillPlayMuted: !!this.props.muted,
        });
      }
    }
  }

  onError(error, errorInfo) {
    if (error.message.match(/Loading chunk.*failed/)) {
      this.setState({ adsBlocked: true });
    } else {
      sentry.captureError(error, errorInfo);
    }
  }

  render() {
    const { isIos, t } = this.props;
    const className = classNames('video-player', this.props.className, {
      'video-player--waiting': !this.state.setupComplete,
    });
    return (
      <div className={className}>
        {this.state.adsBlocked ? (
          <div className="video-player__disable-ad-blocker">
            <span>{t('disable_ad_blocker_to_see_content')}</span>
          </div>
        ) : (
          <div className="video-player--hide" data-vjs-player>
            <React.Fragment>
              <ErrorBoundary onError={this.onError}>
                <Videojs ref={memoizeLoadableLib('videojs')} />
                <VideojsContribAds
                  ref={memoizeLoadableLib('videojsContribAds')}
                />
                <VideojsIMA ref={memoizeLoadableLib('videojsIMA')} />
                <CanAutoplay ref={memoizeLoadableLib('canAutoplay')} />
              </ErrorBoundary>
              <video
                controls
                playsInline={isIos}
                preload="auto"
                className="video-js vjs-waiting vjs-big-play-centered"
                crossOrigin={
                  this.sourceRequiresCredentials()
                    ? 'use-credentials'
                    : undefined
                }
                ref={this.videoElementRef}
              />
            </React.Fragment>
          </div>
        )}
      </div>
    );
  }
}

HTMLVideo.defaultProps = {
  videoEvents: {},
  videoJSOptions: {},
  videoTrackerOptions: {},
  track: false,
  play: false,
  autoPlay: false,
  prerollAds: false,
  forceVideoAds: false,
  isEmbedded: false,
  validThanksSubscription: false,
  midrollTime: 0,
};

HTMLVideo.propTypes = {
  source: PropTypes.string.isRequired,
  videoEvents: PropTypes.object,
  videoJSOptions: PropTypes.object,
  videoTrackerOptions: PropTypes.object,
  track: PropTypes.bool,
  poster: PropTypes.string,
  className: PropTypes.string,
  prerollAds: PropTypes.bool,
  autoPlay: PropTypes.bool,
  play: PropTypes.bool,
  validThanksSubscription: PropTypes.bool,
  adShowPercentage: PropTypes.number.isRequired,
  videoAdTagUrl: PropTypes.string.isRequired,
  overrideAdTagUrl: PropTypes.string,
  shownVideoAd: PropTypes.bool.isRequired,
  setShownVideoAd: PropTypes.func.isRequired,
  refreshSignedMediaCookie: PropTypes.func.isRequired,
  isIos: PropTypes.bool.isRequired,
  sendUsageEventWithSession: PropTypes.func.isRequired,
  videoAdsEnabled: PropTypes.bool.isRequired,
  forceVideoAds: PropTypes.bool,
  isEmbedded: PropTypes.bool,
  onFullscreenChange: PropTypes.func,
  midrollTime: PropTypes.number,
  mimeType: PropTypes.string.isRequired,
  t: PropTypes.func.isRequired,
};

export default connector(
  connectFirstPartyVideo,
  connectSendUsageEventWithSession,
)(withT(withIsIos(withThanksSubscription(HTMLVideo))));
